// schema
import * as ISchema from "#webapp/src/__schema__";

// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
import { VariableSizeList } from "react-window";

// slack-channel-picker imports
import { slackChannelPickerFragments } from "./SlackChannelPicker.gql";
import { styles } from "./SlackChannelPicker.styles";
import { IContainer, IProps, ISlackChannelSuggestion, IState } from "./SlackChannelPickerTypes";

// state
import { IReduxState } from "#webapp/src/state/reducers/root.reducer";

// util
import { escapeRegExp } from "@clockwise/client-commons/src/util/text";
import sanitizeHTML from "sanitize-html";

// material-ui
import { ListSubheader, TextField } from "@material-ui/core";
import { Autocomplete, AutocompleteRenderOptionState } from "@material-ui/lab";

import withStyles from "@material-ui/core/styles/withStyles";

const LISTBOX_PADDING = 0; // px

function renderRow(props: any) {
  const { data, index, style } = props;
  return React.cloneElement(data[index], {
    style: {
      ...style,
      top: style.top + LISTBOX_PADDING,
      padding: "0 6px",
      fontSize: "14px",
    },
  });
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef((props, ref: React.RefObject<HTMLDivElement>) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

const ListboxComponent = React.forwardRef(function ListboxComponent(
  props,
  ref: React.RefObject<HTMLDivElement>,
) {
  const { children, ...other } = props;
  const itemData = React.Children.toArray(children);
  const itemCount = itemData.length;
  const itemSize = 46;

  const getChildSize = (child: React.ReactNode) => {
    if (React.isValidElement(child) && child.type === ListSubheader) {
      return 48;
    }

    return itemSize;
  };

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          key={itemCount}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={(index) => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemCount}
          style={{ padding: `${LISTBOX_PADDING}px 0` }}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

///////////////
// Component //
///////////////
class SlackChannelPickerCmpt extends React.PureComponent<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    const suggestions = this.getSuggestions(props);
    const selectedValue = suggestions.find(
      (suggestion) => suggestion.value === props.value?.channelId,
    );

    this.state = {
      selectedValue,
      searchQuery: "",
    };
  }

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private getSuggestions = (props: IProps, searchQuery?: string): ISlackChannelSuggestion[] => {
    const { org, value } = props;

    const slackChannelList: ISchema.ISlackChannel[] =
      org.publicChannelsForSlackForTeam.__typename === "SlackChannelList"
        ? org.publicChannelsForSlackForTeam.list
        : [];

    let sortedSuggestions = slackChannelList
      .map((c) => ({ label: c.name, value: c.channelId }))
      .sort((a, b) => a.label.localeCompare(b.label));

    // if there is a value and it's not in list, add it
    if (value && slackChannelList.findIndex((c) => c.name === value.name) === -1) {
      sortedSuggestions = [{ label: value.name, value: value.channelId }, ...sortedSuggestions];
    }

    // if searchQuery doesnt have an exact match, append to end of array
    const manuallyEnteredSlackChannel: any[] = [];
    if (searchQuery && sortedSuggestions.findIndex((c) => c.label === searchQuery) === -1) {
      manuallyEnteredSlackChannel.push({
        label: searchQuery,
        value: `CustomSlackEntry-${searchQuery}`,
      });
    }

    return [...sortedSuggestions, ...manuallyEnteredSlackChannel];
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  private renderOption = (option: any, state: AutocompleteRenderOptionState) => {
    const { classes } = this.props;
    const inputValue = escapeRegExp(sanitizeHTML(state.inputValue));

    const re = new RegExp(`${inputValue}`, "gi");

    return (
      <span
        key={option.value}
        className={option.value.startsWith("CustomSlackEntry") ? classes.customEntry : ""}
        dangerouslySetInnerHTML={{
          __html: option.label
            ? option.label.replace(re, `<b style="font-weight: bold;">$&</b>`)
            : option.label,
        }}
      />
    );
  };

  public render() {
    const { classes, disabled, error } = this.props;
    const { selectedValue, searchQuery } = this.state;

    const suggestions = this.getSuggestions(this.props, searchQuery);

    return (
      <Autocomplete
        fullWidth
        disabled={disabled}
        options={suggestions}
        getOptionLabel={(option: ISlackChannelSuggestion) => option.label}
        // defaultValue={selectedValue}
        value={selectedValue}
        disableClearable={true}
        autoHighlight={true}
        ListboxComponent={ListboxComponent as any}
        renderInput={({ inputProps, ...params }) => {
          return (
            <TextField
              {...params}
              error={!selectedValue || error}
              label="Which Slack channel should it post to?"
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                ...inputProps,
                className: classes.input,
              }}
              fullWidth
            />
          );
        }}
        renderOption={this.renderOption}
        onChange={(_e, option) => {
          this.setState({ selectedValue: option });
          this.props.onChange({
            channelId: option.value,
            name: option.label,
          } as ISchema.ISlackChannel);
        }}
        onInputChange={(_e, v, _r) => this.setState({ searchQuery: v })}
      />
    );
  }
}

/////////////////
// With Styles //
/////////////////

const SlackChannelPickerStyled = withStyles(styles)(SlackChannelPickerCmpt);

//////////////////
// REDUX
//////////////////
function mapStateToProps(_state: IReduxState, _ownProps: IContainer) {
  return {};
}

const SlackChannelPickerRedux = connect(mapStateToProps, {})(SlackChannelPickerStyled);

//////////////////
// RELAY
//////////////////
export const SlackChannelPicker = createFragmentContainer<IContainer>(
  SlackChannelPickerRedux,
  slackChannelPickerFragments,
);
